import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CommunityThread } from '@models/community.model';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { AuthService } from '@core/http/auth.service';
import { WebsocketActionType, WebsocketEventType, WebsocketMessageModel } from '@models/websocket.model';
import { CommunityToastComponent } from '@shared/community-toast/community-toast.component';
import { filter, take, takeUntil } from 'rxjs/operators';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { SlugPipe } from '@shared/pipes/slug.pipe';
import { PlatformService } from '@bws-dev/ngx-bws-shared';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  public socketMessages?: WebSocketSubject<WebsocketMessageModel>;

  constructor(private toastr: ToastrService,
              private router: Router,
              private translate: TranslateService,
              private authService: AuthService,
              private localizedRouter: LocalizeRouterService,
              private ps: PlatformService) {

    if (!this.ps.isBrowser) {
      return;
    }

    // Initialize websocket url
    const socketUrl = environment.wssUrl + '?o=MEMBER&ch=' + encodeURIComponent(authService.authenticatedUser?.notificationChannel!);
    this.socketMessages = webSocket(socketUrl);

    // Set notification channel
    this.authService.loginStatusChange
      .subscribe(isLoggedIn => {
        const channel = isLoggedIn ? authService.authenticatedUser?.notificationChannel : 'undefined';
        this.socketMessages!.next({channel: channel} as any);
      });

    // Community Toasts
    this.socketMessages
      .pipe(filter(c => c.event === WebsocketEventType.COMMUNITY && c.action === WebsocketActionType.CREATE))
      .pipe(filter (c => c.category === 'Post' || c.category === 'Thread'))
      .pipe(filter(c => {
        return !this.authService.isLoggedIn() || this.authService.authenticatedUser?.userId !== c?.content?.posts?.[0]?.user?.userId;
      }))
      .subscribe(c => this.handleMessage(c, false));

    // Ticket Toasts
    this.socketMessages
      .pipe(filter(c => c.event === WebsocketEventType.TICKET && c.action === WebsocketActionType.CREATE))
      .pipe(filter (c => c.category === 'Reply'))
      .subscribe(c => this.handleMessage(c, true));
  }

  private handleMessage(message: WebsocketMessageModel, playAudio = true) {

    if (playAudio) {
      try {
        const audio = new Audio();
        audio.src = '/assets/audio/notify.mp3';
        audio.load();
        audio.play();
      } catch (e) {
        // ignored
      }
    }

    if (document.hidden) {
      const handler = () => {
        document.removeEventListener('visibilitychange', handler);
        this.showToast(message);
      };
      document.addEventListener('visibilitychange', handler);
    } else {
      this.showToast(message);
    }

  }

  private showToast(message: WebsocketMessageModel) {
    switch (message.event) {

      case WebsocketEventType.COMMUNITY:
        switch (message.action) {
          case WebsocketActionType.CREATE:
            switch (message.category) {
              case 'Post':
                const createdPost: CommunityThread = message.content as CommunityThread;
                /*
                const doc = new DOMParser().parseFromString(createdPost.posts[0].body, 'text/html');
                const element = doc.getElementsByTagName('blockquote');
                let index;
                for (index = element.length - 1; index >= 0; index--) {
                  element[index].parentNode.removeChild(element[index]);
                }
                 */
                const newPostMsg = this.translate.instant('GLOBAL.COMMUNITY-PUSH.NEW-POST', {
                  user: createdPost.posts[0].user.nickname,
                });
                const newPostToast = this.toastr.info(newPostMsg, createdPost.header, {
                  timeOut: 6000, // 6s
                  extendedTimeOut: 1000, // 1s
                  positionClass: 'toast-bottom-center',
                  toastClass: 'community-toast',
                  toastComponent: CommunityToastComponent
                });
                newPostToast.onTap
                  .pipe(takeUntil(newPostToast.onHidden), take(1))
                  .subscribe(() => {
                    const slugPipe = new SlugPipe();
                    const route = `${this.localizedRouter.translateRoute(`/forum/`)}/` +
                      `${slugPipe.transform(createdPost.categoryName)}-${createdPost.categoryId}/threads/`+
                      `${slugPipe.transform(createdPost.header)}-${createdPost.threadId}`;
                    this.router.navigate([route], {queryParams: { post: createdPost.posts[0].postId }});
                  });
                break;

              case 'Thread':
                const thread: CommunityThread = message.content as CommunityThread;
                const newThreadMsg = this.translate.instant('GLOBAL.COMMUNITY-PUSH.NEW-THREAD', {
                  user: thread.user.nickname,
                  threadName: thread.header
                });
                const newThreadToast = this.toastr.info(newThreadMsg, '', {
                  timeOut: 6000, // 6s
                  extendedTimeOut: 1000, // 1s
                  positionClass: 'toast-bottom-center',
                  toastClass: 'community-toast',
                  toastComponent: CommunityToastComponent
                });
                newThreadToast.onTap
                  .pipe(takeUntil(newThreadToast.onHidden), take(1))
                  .subscribe(() => {
                    const slugPipe = new SlugPipe();
                    const route = `${this.localizedRouter.translateRoute(`/forum/`)}/` +
                      `${slugPipe.transform(thread.categoryName)}-${thread.categoryId}/threads/`+
                      `${slugPipe.transform(thread.header)}-${thread.threadId}`;
                    this.router.navigate([route]);
                  });
                break;
            }
            break;
        }
        break;

      case WebsocketEventType.TICKET:
        if (message.action === WebsocketActionType.CREATE && message.category === 'Reply') {
          const timeout = 10 * 1000; // 10 sec
          const msgHead = this.translate.instant('GLOBAL.INFO');
          const msgBody = this.translate.instant('GLOBAL.WEBSOCKET.TICKET-REPLY');
          const toast = this.toastr.info(msgBody, msgHead, {extendedTimeOut: timeout});
          const toastrSub = toast.onTap.subscribe(() => {
            const route = this.localizedRouter.translateRoute(`/member/support/tickets`);
            this.router.navigate([route, message.reference]);
            toastrSub.unsubscribe();
          });
        }
        break;
    }
  }

}
